/**
 * Instagram-story progress card (issue #16): a 1080×1920 PNG drawn on a canvas
 * from the user's real numbers, shared through the OS sheet as a file (so it can
 * go to a story) with a download fallback. Motivation over statistics: the big
 * number is the streak, the rest supports it (docs/CLAUDE.md, regla de oro 3).
 */

export interface StoryCardData {
  days: number;
  savings: string;
  avoided: string;
  cravings: string;
  labels: {
    days: string;
    saved: string;
    avoided: string;
    cravings: string;
    tagline: string;
  };
}

const W = 1080;
const H = 1920;
const FONT = "'Segoe UI', system-ui, -apple-system, Roboto, sans-serif";

export function buildStoryCard(data: StoryCardData): Promise<Blob> {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  // Deep-green vertical gradient, fixed (not theme-dependent): the image lives
  // outside the app once shared, so it shouldn't follow the viewer's theme.
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#16a97b');
  bg.addColorStop(0.55, '#0b6b4d');
  bg.addColorStop(1, '#05372a');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  const center = (text: string, y: number, size: number, weight = '700', alpha = 1) => {
    ctx.globalAlpha = alpha;
    ctx.fillStyle = '#ffffff';
    ctx.font = `${weight} ${size}px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.fillText(text, W / 2, y);
    ctx.globalAlpha = 1;
  };

  // Wordmark + tagline
  center('Respiria', 230, 76, '800');
  center(data.labels.tagline, 300, 34, '500', 0.85);

  // The hero: the streak.
  center(String(data.days), 760, 340, '800');
  center(data.labels.days, 900, 58, '600', 0.95);

  // Supporting stats, each an emoji + value + label, evenly stacked.
  const stat = (emoji: string, value: string, label: string, y: number) => {
    center(`${emoji}  ${value}`, y, 76, '700');
    center(label, y + 62, 40, '500', 0.8);
  };
  stat('💶', data.savings, data.labels.saved, 1200);
  stat('🚭', data.avoided, data.labels.avoided, 1400);
  stat('💪', data.cravings, data.labels.cravings, 1600);

  center('respiria.isaies.com', 1830, 42, '600', 0.9);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))), 'image/png');
  });
}

/**
 * Shares an image file through the OS share sheet when the platform allows file
 * sharing (most mobile browsers); otherwise downloads the PNG so the user can
 * post it to a story by hand.
 */
export async function shareImage(blob: Blob, filename: string, text?: string): Promise<void> {
  const file = new File([blob], filename, { type: 'image/png' });
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  if (nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], text });
    } catch {
      // Dismissed — nothing to do.
    }
    return;
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
